import React, { useState } from 'react';
import { SearchIcon, FilterIcon, ChevronLeftIcon, ChevronRightIcon, EyeIcon, EditIcon, CheckIcon, TrashIcon, DownloadIcon, XIcon } from 'lucide-react';
interface Column {
  key: string;
  label: string;
  render?: (value: any, row: any) => React.ReactNode;
}
interface Filter {
  key: string;
  label: string;
  options: string[];
}
interface DataTableProps {
  columns: Column[];
  data: any[];
  filters?: Filter[];
  onView?: (row: any) => void;
  onEdit?: (row: any) => void;
  onApprove?: (row: any) => void;
  onDelete?: (row: any) => void;
  onDataChange?: (newData: any[]) => void;
  searchPlaceholder?: string;
}
export function DataTable({
  columns,
  data,
  filters,
  onView,
  onEdit,
  onApprove,
  onDelete,
  onDataChange,
  searchPlaceholder = 'Search...'
}: DataTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // Modal state
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'delete' | null>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [editFormData, setEditFormData] = useState<any>({});
  const itemsPerPage = 10;
  const filteredData = data.filter(row => Object.values(row).some(value => String(value).toLowerCase().includes(searchTerm.toLowerCase())));
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);
  const handleAction = (action: 'view' | 'edit' | 'delete', row: any) => {
    if (onDataChange) {
      setSelectedRow(row);
      setEditFormData({
        ...row
      });
      setModalMode(action);
    } else {
      // Fallback to legacy callbacks
      if (action === 'view' && onView) onView(row);
      if (action === 'edit' && onEdit) onEdit(row);
      if (action === 'delete' && onDelete) onDelete(row);
    }
  };
  const handleSaveEdit = () => {
    if (onDataChange && selectedRow) {
      const newData = data.map(item => item === selectedRow ? editFormData : item);
      onDataChange(newData);
      setModalMode(null);
    }
  };
  const handleConfirmDelete = () => {
    if (onDataChange && selectedRow) {
      const newData = data.filter(item => item !== selectedRow);
      onDataChange(newData);
      setModalMode(null);
    }
  };
  return <div className="space-y-4 relative" data-id="element-187" data-mp="1ycfi2a4da5yk">
      {/* Search and Filters */}
      <div className="flex items-center justify-between gap-4" data-id="element-188" data-mp="1tjw2j7m4d0r4">
        <div className="flex items-center gap-4 flex-1" data-id="element-189" data-mp="1ol1cplpqibxi">
          <div className="flex-1 relative max-w-md" data-id="element-190" data-mp="1olkun2fdx0bj">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-191" data-mp="1kjpw25ou911g" />
            <input type="text" placeholder={searchPlaceholder} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-192" data-mp="1fjldma4ddw8l" />
          </div>
          {filters && filters.map(filter => <select key={filter.key} className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-193" data-mp="3w4oyc027t4c3">
                <option value="" data-id="element-194" data-mp="33g1bi01ti6wf">{filter.label}</option>
                {filter.options.map(option => <option key={option} value={option} data-id="element-195" data-mp="38eun1mxjnnty">
                    {option}
                  </option>)}
              </select>)}
        </div>
        <div className="flex items-center gap-2" data-id="element-196" data-mp="3hatwvf4e2hax">
          <button onClick={() => alert('Exporting to Excel...')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-197" data-mp="2ks62eth5hh9n">
            <DownloadIcon size={16} data-id="element-198" data-mp="25mihi8swxf2v" />
            Export Excel
          </button>
          <button onClick={() => alert('Exporting to PDF...')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-199" data-mp="20r357lajzr8k">
            <DownloadIcon size={16} data-id="element-200" data-mp="2oekyxmddoze6" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-201" data-mp="2jf82qb0mafyx">
        <div className="overflow-x-auto" data-id="element-202" data-mp="2ydk1qobgq0t9">
          <table className="w-full" data-id="element-203" data-mp="2ycil84bogm45">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-204" data-mp="2tegwb2rklskr">
              <tr data-id="element-205" data-mp="2tc4h2xd75wbe">
                {columns.map(column => <th key={column.key} className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-206" data-mp="1402aaq7xsjsy">
                    {column.label}
                  </th>)}
                {(onView || onEdit || onApprove || onDelete || onDataChange) && <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-207" data-mp="30xge53zdvc20">
                    Actions
                  </th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-208" data-mp="2vyn8fxqc8pce">
              {paginatedData.map((row, idx) => <tr key={idx} className="hover:bg-gray-50 transition-colors" data-id="element-209" data-mp="2hz40p61fvg0m" data-mpi={idx}>
                  {columns.map(column => <td key={column.key} className="px-6 py-4 text-sm text-gray-900" data-id="element-210" data-mp="2mx5mhyq91tfs">
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </td>)}
                  {(onView || onEdit || onApprove || onDelete || onDataChange) && <td className="px-6 py-4 text-sm" data-id="element-211" data-mp="9saat2cjll8y">
                      <div className="flex items-center gap-2" data-id="element-212" data-mp="4s5rz5o5wefk">
                        {(onView || onDataChange) && <button onClick={() => handleAction('view', row)} className="p-1.5 text-gray-600 hover:text-[#1B5E20] hover:bg-gray-100 rounded transition-colors" title="View" data-id="element-213" data-mp="12gfwbrbf7e9u">
                            <EyeIcon size={16} data-id="element-214" data-mp="nrjq7zsxcbbv" />
                          </button>}
                        {(onEdit || onDataChange) && <button onClick={() => handleAction('edit', row)} className="p-1.5 text-gray-600 hover:text-[#1B5E20] hover:bg-gray-100 rounded transition-colors" title="Edit" data-id="element-215" data-mp="1l8gzh5v89qp2">
                            <EditIcon size={16} data-id="element-216" data-mp="1srzukahqxae0" />
                          </button>}
                        {onApprove && <button onClick={() => onApprove(row)} className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-gray-100 rounded transition-colors" title="Approve" data-id="element-217" data-mp="22p4gc97oj3ua">
                            <CheckIcon size={16} data-id="element-218" data-mp="2gf7vkg855xa8" />
                          </button>}
                        {(onDelete || onDataChange) && <button onClick={() => handleAction('delete', row)} className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-gray-100 rounded transition-colors" title="Delete" data-id="element-219" data-mp="2qdnq6t6dnooh">
                            <TrashIcon size={16} data-id="element-220" data-mp="3bom7gkxb4723" />
                          </button>}
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-221" data-mp="3knhbbqcvp528">
          <div className="text-sm text-gray-700" data-id="element-222" data-mp="2wxd99nfc9ddc">
            Showing {startIndex + 1}-
            {Math.min(startIndex + itemsPerPage, filteredData.length)} of{' '}
            {filteredData.length}
          </div>
          <div className="flex items-center gap-2" data-id="element-223" data-mp="31z2nfbxuy75n">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-224" data-mp="31x9d9mo1d4sw">
              <ChevronLeftIcon size={16} data-id="element-225" data-mp="or1n11ffoz3m" />
            </button>
            {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 rounded-lg ${currentPage === page ? 'bg-[#1B5E20] text-white' : 'border border-gray-300 hover:bg-gray-50'}`} data-id="element-226" data-mp="fszfe317387d">
                {page}
              </button>)}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-227" data-mp="1cfcjlse3kcm3">
              <ChevronRightIcon size={16} data-id="element-228" data-mp="3jahpkh4fwray" />
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {modalMode && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-229" data-mp="2i987lat3uys3">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col" data-id="element-230" data-mp="2n5ynny4wtudi">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-231" data-mp="28hknxw2sim16">
              <h3 className="text-lg font-semibold text-gray-900 capitalize" data-id="element-232" data-mp="2dg5ltoukktmg">
                {modalMode} Record
              </h3>
              <button onClick={() => setModalMode(null)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-233" data-mp="1zjs3kdi7oo6x">
                <XIcon size={20} data-id="element-234" data-mp="1pl2mgmy537rq" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1" data-id="element-235" data-mp="1uht3pdlok9eq">
              {modalMode === 'view' && <div className="space-y-4" data-id="element-236" data-mp="3jx970ccxb0wp">
                  {columns.map(col => <div key={col.key} className="grid grid-cols-3 gap-4 border-b border-gray-100 pb-3 last:border-0" data-id="element-237" data-mp="3ox5bsdtg2wdb">
                      <div className="text-sm font-medium text-gray-500" data-id="element-238" data-mp="18afkl5w3n9j1">
                        {col.label}
                      </div>
                      <div className="col-span-2 text-sm text-gray-900" data-id="element-239" data-mp="1mzv8me48x0v1">
                        {col.render ? col.render(selectedRow[col.key], selectedRow) : String(selectedRow[col.key] || '-')}
                      </div>
                    </div>)}
                </div>}

              {modalMode === 'edit' && <div className="space-y-4" data-id="element-240" data-mp="25wmoseohqzuv">
                  {columns.map(col => <div key={col.key} data-id="element-241" data-mp="20zw9vuofaab1">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-242" data-mp="20wru001lhq4j">
                        {col.label}
                      </label>
                      {/* Skip rendering complex objects or use simple text inputs */}
                      <input type="text" value={editFormData[col.key] || ''} onChange={e => setEditFormData({
                ...editFormData,
                [col.key]: e.target.value
              })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-243" data-mp="w9h9stx3klca" />
                    </div>)}
                </div>}

              {modalMode === 'delete' && <div className="text-center py-4" data-id="element-244" data-mp="1o285926yi60h">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-245" data-mp="1szhv2iy9cjou">
                    <TrashIcon className="text-red-600" size={32} data-id="element-246" data-mp="1xzm81yzyiwab" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2" data-id="element-247" data-mp="22yh07rjtdx73">
                    Confirm Deletion
                  </h4>
                  <p className="text-gray-500" data-id="element-248" data-mp="3nddh0bwnbe1c">
                    Are you sure you want to delete this record? This action
                    cannot be undone.
                  </p>
                </div>}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-xl" data-id="element-249" data-mp="38ipft9ya31rj">
              <button onClick={() => setModalMode(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-250" data-mp="a0tnjilt3n33">
                {modalMode === 'view' ? 'Close' : 'Cancel'}
              </button>
              {modalMode === 'edit' && <button onClick={handleSaveEdit} className="px-4 py-2 text-sm font-medium text-white bg-[#1B5E20] rounded-lg hover:bg-[#2E7D32]" data-id="element-251" data-mp="3rez1ojff8wm3">
                  Save Changes
                </button>}
              {modalMode === 'delete' && <button onClick={handleConfirmDelete} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700" data-id="element-252" data-mp="dheo5w8vwyou">
                  Delete Record
                </button>}
            </div>
          </div>
        </div>}
    </div>;
}